import React, { useEffect, useState } from 'react';
import { Navigate, Link, useParams, useLocation } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import { vendorPages, getVendorPage, CAPABILITIES, BOARD_STATES } from './vendorPages';
import { captureAttribution, readAttribution, trackLead } from '../utils/attribution';
import './PricingPage.css';
import './RateCard.css';
import './VendorLandingPage.css';

const ENTERPRISE_ENDPOINT = 'https://audit.ubotika.site/enterprise';

// Campaign chrome. The site nav (and its competing "Free Audit" CTA) is suppressed
// in App.js for this namespace; these give a single deliberate way out plus the
// legal links ad platforms require.
function CampaignTopBar() {
  return (
    <div className="vs-topbar">
      <Link to="/" className="vs-topbar-brand">
        <img src="/ubotika-logo.png" alt="" aria-hidden="true" />
        <span>UBOTIKA</span>
      </Link>
      <Link to="/" className="vs-topbar-home">Return to home</Link>
    </div>
  );
}

function CampaignFooter() {
  return (
    <footer className="vs-footer">
      <span>&copy; {new Date().getFullYear()} UBOTIKA · Los Angeles, CA</span>
      <span className="vs-footer-links">
        <a href="mailto:hello@ubotika.com">hello@ubotika.com</a>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/">Return to home</Link>
      </span>
    </footer>
  );
}


// Lead form for the LinkedIn campaign. Posts to /enterprise (owner email only) —
// never /submit, which fires an automated *website* audit that would be nonsense
// for a compliance buyer. Carries source + UTMs + landingPath for attribution.
function VendorLeadForm({ slug, ctaLabel, ctaNote }) {
  const [firm, setFirm] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const { pathname } = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (hp) return;
    if (!firm.trim()) { setErrorMsg('Please enter your company name.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErrorMsg('Please enter a valid work email.'); return; }
    setStatus('sending');
    setErrorMsg('');
    const source = `li-vendor-${slug}`;
    try {
      const res = await fetch(ENTERPRISE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firm: firm.trim(),
          name: name.trim(),
          email: email.trim(),
          role: 'Vendor screening enquiry',
          note: note.trim(),
          source,
          landingPath: pathname,
          utm: readAttribution(),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('success');
      trackLead({ source, landingPath: pathname });
    } catch (err) {
      setStatus('error');
      setErrorMsg('Could not send. Please email hello@ubotika.com directly.');
    }
  };

  if (status === 'success') {
    return (
      <div className="vs-form vs-form--done">
        <h3>Got it.</h3>
        <p>
          We&apos;ll reply from hello@ubotika.com within one business day with the screening
          report and how we produced it — no sales sequence.
        </p>
      </div>
    );
  }

  return (
    <form className="vs-form" onSubmit={onSubmit} noValidate>
      <h3>{ctaLabel}</h3>
      <p className="vs-form-lede">{ctaNote}</p>

      <div className="vs-field">
        <label htmlFor="vs-firm">Your company</label>
        <input id="vs-firm" type="text" value={firm} onChange={(e) => setFirm(e.target.value)} required />
      </div>
      <div className="vs-field-row">
        <div className="vs-field">
          <label htmlFor="vs-name">Your name</label>
          <input id="vs-name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </div>
        <div className="vs-field">
          <label htmlFor="vs-email">Work email</label>
          <input id="vs-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        </div>
      </div>
      <div className="vs-field">
        <label htmlFor="vs-note">Vendor or counterparty to screen <span>(optional)</span></label>
        <textarea id="vs-note" rows="3" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      <input
        type="text" name="website_url_confirm" tabIndex="-1" autoComplete="off"
        aria-hidden="true" className="vs-hp" value={hp} onChange={(e) => setHp(e.target.value)}
      />

      {errorMsg && <p className="vs-form-error">{errorMsg}</p>}

      <button type="submit" className="cta-button vs-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Request a screening'}
      </button>
      <p className="vs-form-fine">
        Goes straight to us. Prefer email?{' '}
        <a href="mailto:hello@ubotika.com?subject=Vendor%20screening">hello@ubotika.com</a>
      </p>
    </form>
  );
}

export function VendorScreeningIndex() {
  useEffect(() => { captureAttribution(); }, []);

  return (
    <>
      <SEO
        title="Third-Party & Vendor Risk Screening | Vendor Due Diligence | UBOTIKA"
        description="Third-party risk screening and vendor due diligence in under a minute: contractor license verification, lawsuits, judgments, liens, complaints, regulatory actions, and news — a source URL on every claim and a CLEAR, REVIEW, or DO NOT ENGAGE verdict."
        path="/vendor-screening"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'AI Third-Party & Vendor Risk Screening',
            serviceType: 'Third-party risk screening, vendor due diligence, and contractor license verification',
            description:
              'Automated third-party risk screening across license boards, courts, liens, complaints, regulatory actions, and news, returning a sourced CLEAR / REVIEW / DO NOT ENGAGE verdict.',
            provider: {
              '@type': 'Organization',
              '@id': `${SITE_URL}/#organization`,
              name: 'UBOTIKA',
              url: SITE_URL,
              email: 'hello@ubotika.com',
            },
            offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', priceCurrency: 'USD' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'Vendor Screening', item: `${SITE_URL}/vendor-screening` },
            ],
          },
        ]}
      />
      <main id="main-content" className="pricing-page ratecard-page vs-page">
        <CampaignTopBar />
        <section className="pricing-hero">
          <p className="vs-eyebrow">Third-party &amp; vendor risk screening</p>
          <h1>Third-party risk screening: know who you&apos;re about to do business with.</h1>
          <p className="pricing-sub">
            Vendor due diligence without the tab-opening. One company name in, a sourced verdict out — licenses, litigation, liens,
            complaints, regulatory actions, and news — in 15 to 45 seconds, with the URL
            behind every claim.
          </p>
        </section>

        <section className="ratecard-section">
          <h2>What a screen covers</h2>
          <div className="ratecard-grid">
            {CAPABILITIES.map((c) => (
              <article className="ratecard-vertical" key={c.title}>
                <h3>{c.title}</h3>
                <p className="vs-body">{c.body}</p>
              </article>
            ))}
          </div>
          <p className="ratecard-fine">
            Direct board verification covers {BOARD_STATES.join(', ')}. Outside those states a
            screen returns web-search pointers, which we label as research rather than
            verification — we would rather say so than imply 50-state coverage.
          </p>
        </section>

        <section className="ratecard-section">
          <h2>Start anywhere</h2>
          <div className="vs-index-grid">
            {vendorPages.map((p) => (
              <Link key={p.slug} to={`/vendor-screening/${p.slug}`} className="vs-index-card">
                <span className="vs-index-title">{p.h1}</span>
                <span className="vs-index-cue">Read more →</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="ratecard-pilot vs-cta">
          <div>
            <h2>Screen one vendor, free.</h2>
            <p>
              Send a company name you already work with. We&apos;ll run a real screen and send
              back the report — verdict, evidence, and every source — so you can judge the
              output rather than the pitch.
            </p>
          </div>
          <VendorLeadForm
            slug="index"
            ctaLabel="Request a screening"
            ctaNote="One name is enough. We'll send the full report back the same day."
          />
        </section>
        <CampaignFooter />
      </main>
    </>
  );
}

function VendorLandingPage() {
  const { slug } = useParams();
  const page = getVendorPage(slug);

  useEffect(() => { captureAttribution(); }, []);

  if (!page) return <Navigate to="/vendor-screening" replace />;

  return (
    <>
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/vendor-screening/${page.slug}`}
        noindex
      />
      <main id="main-content" className="pricing-page ratecard-page vs-page">
        <CampaignTopBar />
        <section className="pricing-hero">
          <p className="vs-eyebrow">{page.eyebrow}</p>
          <h1>{page.h1}</h1>
          <p className="pricing-sub">{page.sub}</p>
        </section>

        <section className="ratecard-section">
          <div className="vs-points">
            {page.points.map((pt) => (
              <div className="vs-point" key={pt}>
                <FaCheck />
                <span>{pt}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="ratecard-section">
          <h2>What a screen actually covers</h2>
          <div className="ratecard-grid">
            {CAPABILITIES.map((c) => (
              <article className="ratecard-vertical" key={c.title}>
                <h3>{c.title}</h3>
                <p className="vs-body">{c.body}</p>
              </article>
            ))}
          </div>
          <p className="ratecard-fine">
            Direct board verification covers {BOARD_STATES.join(', ')} only. Outside those
            states a screen returns web-search pointers — research, not verification. Stated
            plainly because you&apos;re the one who has to defend the file.
          </p>
        </section>

        <section className="ratecard-pilot vs-cta">
          <div>
            <h2>{page.cta}</h2>
            <p>{page.ctaNote}</p>
            <ul className="vs-cta-points">
              <li>A real report on a real company — not a canned sample</li>
              <li>Every finding linked to the record it came from</li>
              <li>No sales sequence; we reply once, from hello@ubotika.com</li>
            </ul>
          </div>
          <VendorLeadForm slug={page.slug} ctaLabel={page.cta} ctaNote={page.ctaNote} />
        </section>

        <section className="ratecard-section">
          <p className="ratecard-fine">
            <Link to="/vendor-screening">All vendor-screening pages</Link> ·{' '}
            <Link to="/web-apps">Other systems we build</Link>
          </p>
        </section>
        <CampaignFooter />
      </main>
    </>
  );
}

export default VendorLandingPage;
